import { useEffect } from "react";
import AnfitriaoPerfilEditar from "@/pages/AnfitriaoPerfilEditarFotosFix";

type AnfitriaoPerfilEditarSincronizadoProps = {
  embedded?: boolean;
};

const identificarUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const identificarMetodo = (
  input: RequestInfo | URL,
  init?: RequestInit,
): string => {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
};

const ehSalvamentoDePerfil = (
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean => {
  const url = identificarUrl(input);
  const metodo = identificarMetodo(input, init);

  return metodo === "POST" && url.includes("/anfitriao/perfil-dados");
};

const removerCamposDeServicos = (formData: FormData): void => {
  const chaves: string[] = [];

  formData.forEach((_valor, chave) => {
    if (/^servicos\[\d+\]$/.test(chave)) {
      chaves.push(chave);
    }
  });

  chaves.forEach((chave) => formData.delete(chave));
};

const sincronizarServicosVisiveis = (
  formData: FormData,
  servicoVeterinarioSelecionado: boolean | null,
): void => {
  const tipo = String(formData.get("tipo") || "pf");
  // Perfis veterinários legados aprovados podem ter tipo PF cadastrado,
  // mas o serviço 7 deve continuar visível e editável para esses profissionais.
  const veterinarioVisivel = Boolean(document.querySelector<HTMLInputElement>('input[type="checkbox"]#serv7'));
  const idsPermitidos = tipo === "pf" && !veterinarioVisivel
    ? [1, 2, 3, 4, 5, 6, 8]
    : [1, 2, 3, 4, 5, 6, 7, 8];

  removerCamposDeServicos(formData);

  const idsMarcados = idsPermitidos.filter((id) => {
    if (id === 7 && servicoVeterinarioSelecionado !== null) {
      return servicoVeterinarioSelecionado;
    }

    const checkbox = document.querySelector<HTMLInputElement>(
      `input[type="checkbox"]#serv${id}`,
    );

    return checkbox?.checked === true;
  });

  idsMarcados.forEach((id, index) => {
    formData.append(`servicos[${index}]`, String(id));
  });
};

export default function AnfitriaoPerfilEditarSincronizado({
  embedded = false,
}: AnfitriaoPerfilEditarSincronizadoProps) {
  useEffect(() => {
    const fetchOriginal = window.fetch.bind(window);
    const formulario = document.querySelector<HTMLFormElement>("form");
    const noValidateAnterior = formulario?.noValidate ?? false;
    let servicoVeterinarioSelecionado: boolean | null = null;

    if (formulario) {
      formulario.noValidate = true;
    }

    const registrarMudancaServicoVeterinario = (event: Event) => {
      const target = event.target;

      if (
        target instanceof HTMLInputElement &&
        target.type === "checkbox" &&
        target.id === "serv7"
      ) {
        servicoVeterinarioSelecionado = target.checked;
      }
    };

    document.addEventListener(
      "change",
      registrarMudancaServicoVeterinario,
      true,
    );

    const fetchSincronizado: typeof window.fetch = async (input, init) => {
      if (
        ehSalvamentoDePerfil(input, init) &&
        init?.body instanceof FormData
      ) {
        sincronizarServicosVisiveis(
          init.body,
          servicoVeterinarioSelecionado,
        );
      }

      return fetchOriginal(input, init);
    };

    window.fetch = fetchSincronizado;

    return () => {
      document.removeEventListener(
        "change",
        registrarMudancaServicoVeterinario,
        true,
      );

      if (formulario) {
        formulario.noValidate = noValidateAnterior;
      }

      if (window.fetch === fetchSincronizado) {
        window.fetch = fetchOriginal;
      }
    };
  }, []);

  return <AnfitriaoPerfilEditar embedded={embedded} />;
}
